/**
 * SubscriptionContext - Global subscription state management
 * Prevents multiple API calls by sharing subscription data across all components
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/firebase/useAuth';
import { SubscriptionService } from '../services/subscription/subscriptionService';
import { buildApiUrl } from '../config/api.config';
import { getAuthTokenAsync, notifySessionExpired } from '../services/api/authTokenManager';
import type { SubscriptionTier, UserSubscription } from '../types/user';
import type { FeatureIdentifier } from '../types/subscription';

interface TrialState {
  eligible: boolean;
  active: { expiresAt: string; daysLeft: number } | null;
}

interface SubscriptionState {
  tier: SubscriptionTier;
  subscription: UserSubscription | null;
  limits: ReturnType<typeof SubscriptionService.getSubscriptionLimits>;
  signalsUsedToday: number;
  loading: boolean;
  error: string | null;
  trial: TrialState;
}

interface SubscriptionActions {
  canAccessSignal: () => boolean;
  canAccessInstrument: (symbol: string) => boolean;
  canAccessFeature: (feature: FeatureIdentifier) => boolean;
  getRemainingSignals: () => number;
  getUpgradeMessage: (feature: FeatureIdentifier) => string;
  needsUpgrade: (feature: FeatureIdentifier) => boolean;
  shouldShowUpgradePrompt: () => boolean;
  refreshUsage: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  startTrial: () => Promise<boolean>;
}

interface SubscriptionComputedProps {
  isTrialing: boolean;
}

type SubscriptionContextType = SubscriptionState & SubscriptionActions & SubscriptionComputedProps;

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

// API response interfaces
interface SubscriptionApiResponse {
  success: boolean;
  data: {
    subscription: UserSubscription;
    usage?: {
      signalsToday: number;
    };
  };
}

interface UsageApiResponse {
  success: boolean;
  data: {
    signalsToday: number;
  };
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [signalsUsedToday, setSignalsUsedToday] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trial, setTrial] = useState<TrialState>({ eligible: true, active: null });

  // Default to FREE tier if no subscription data
  const tier: SubscriptionTier = subscription?.tier ?? 'FREE';
  const limits = SubscriptionService.getSubscriptionLimits(tier);
  const isTrialing = trial.active !== null && trial.active.daysLeft > 0;

  // Fetch user subscription data - SINGLE INSTANCE
  const fetchSubscriptionData = useCallback(async (userId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch subscription data from API
      const token = await getAuthTokenAsync();
      const response = await fetch(
        buildApiUrl(`/api/users/${userId}/subscription`),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
        }
      );

      if (response.status === 401) {
        notifySessionExpired();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json() as SubscriptionApiResponse;

      if (result.success) {
        setSubscription(result.data.subscription);
        setSignalsUsedToday(result.data.usage?.signalsToday ?? 0);
      } else {
        // Default to FREE tier if no subscription found
        setSubscription({
          tier: 'FREE',
          status: 'active',
          features: ['basic_signals'] as FeatureIdentifier[],
          billingCycle: 'monthly',
          cancelAtPeriodEnd: false,
          usageStats: {
            signalsGenerated: 0,
            apiCalls: 0,
            notifications: 0
          }
        });
        setSignalsUsedToday(0);
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load subscription data');
      
      // Default to FREE tier on error
      setSubscription({
        tier: 'FREE',
        status: 'active',
        features: ['basic_signals', 'email_notifications'] as FeatureIdentifier[],
        billingCycle: 'monthly',
        cancelAtPeriodEnd: false,
        usageStats: {
          signalsGenerated: 0,
          apiCalls: 0,
          notifications: 0
        }
      });
      setSignalsUsedToday(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh usage data
  const refreshUsage = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const usageToken = await getAuthTokenAsync();
      const response = await fetch(
        buildApiUrl(`/api/users/${user.uid}/usage`),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(usageToken ? { 'Authorization': `Bearer ${usageToken}` } : {}),
          },
        }
      );

      if (response.status === 401) {
        notifySessionExpired();
        return;
      }

      if (response.ok) {
        const result = await response.json() as UsageApiResponse;
        if (result.success) {
          setSignalsUsedToday(result.data.signalsToday ?? 0);
        }
      }
    } catch (err) {
      console.error('Error refreshing usage data:', err);
    }
  }, [user?.uid]);

  // Refresh complete subscription data (for after payment upgrades)
  const refreshSubscription = useCallback(async () => {
    if (!user?.uid) return;
    
    try {
      setLoading(true);
      await fetchSubscriptionData(user.uid);
    } catch (err) {
      console.error('Error refreshing subscription data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, fetchSubscriptionData]);

  // Fetch tier configs from API on mount (non-blocking)
  useEffect(() => {
    void SubscriptionService.fetchTierConfigs();
  }, []);

  // Fetch trial eligibility
  const fetchTrialEligibility = useCallback(async (_userId: string) => {
    try {
      const token = await getAuthTokenAsync();
      const response = await fetch(
        buildApiUrl('/api/trial/eligibility'),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
        }
      );
      if (response.status === 401) {
        notifySessionExpired();
        return;
      }
      if (response.ok) {
        const result = await response.json() as { success: boolean; data: { eligible: boolean; activeTrial?: TrialState['active']; active?: TrialState['active'] } };
        if (result.success) {
          setTrial({ eligible: result.data.eligible, active: result.data.activeTrial ?? result.data.active ?? null });
        }
      }
    } catch {
      // Trial check is non-blocking
    }
  }, []);

  // Start trial
  const startTrial = useCallback(async (): Promise<boolean> => {
    if (!user?.uid) return false;
    try {
      const token = await getAuthTokenAsync();
      const response = await fetch(
        buildApiUrl('/api/trial/start'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
        }
      );
      if (response.status === 401) {
        notifySessionExpired();
        return false;
      }
      const result = await response.json() as { success: boolean; data?: { expiresAt: string }; error?: string };
      if (result.success) {
        // Refresh subscription data to reflect new tier
        await fetchSubscriptionData(user.uid);
        await fetchTrialEligibility(user.uid);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [user?.uid, fetchSubscriptionData, fetchTrialEligibility]);

  // Single useEffect - only triggers when user changes
  useEffect(() => {
    if (user?.uid) {
      void fetchSubscriptionData(user.uid);
      void fetchTrialEligibility(user.uid);
    } else {
      setLoading(false);
    }
  }, [user?.uid, fetchSubscriptionData, fetchTrialEligibility]);

  // Auto-refresh trial state every 5 minutes when trial is active
  useEffect(() => {
    if (!user?.uid || !trial.active) return;
    const intervalId = setInterval(() => {
      void fetchTrialEligibility(user.uid);
      void refreshUsage();
    }, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [user?.uid, trial.active?.expiresAt, fetchTrialEligibility, refreshUsage]);

  // Action functions
  const canAccessSignal = () => SubscriptionService.canAccessSignal(tier, signalsUsedToday);
  const canAccessInstrument = (symbol: string) => SubscriptionService.canAccessInstrument(tier, symbol);
  const canAccessFeature = (feature: FeatureIdentifier) => SubscriptionService.canAccessFeature(tier, feature as Parameters<typeof SubscriptionService.canAccessFeature>[1]);
  const getRemainingSignals = () => SubscriptionService.getRemainingSignals(tier, signalsUsedToday);
  const getUpgradeMessage = (feature: FeatureIdentifier) => SubscriptionService.getUpgradeMessage(tier, feature as Parameters<typeof SubscriptionService.getUpgradeMessage>[1]);
  const needsUpgrade = (feature: FeatureIdentifier) => SubscriptionService.needsUpgrade(tier, feature as Parameters<typeof SubscriptionService.needsUpgrade>[1]);
  const shouldShowUpgradePrompt = () => SubscriptionService.shouldShowUpgradePrompt(tier, signalsUsedToday);

  const value: SubscriptionContextType = {
    // State
    tier,
    subscription,
    limits,
    signalsUsedToday,
    loading,
    error,
    trial,
    isTrialing,

    // Actions
    canAccessSignal,
    canAccessInstrument,
    canAccessFeature,
    getRemainingSignals,
    getUpgradeMessage,
    needsUpgrade,
    shouldShowUpgradePrompt,
    refreshUsage,
    refreshSubscription,
    startTrial,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// Hook to use subscription context
export function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
}